import { Card, CardActionArea, CardContent, Typography, CardHeader, IconButton } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import React, { useState } from "react";
import { Close, Launch } from "@material-ui/icons";
import CustomDraggableDialog from "./customDialog/CustomDraggableDialog";
import TranslatedMessage from "../translations/TranslatedMessage";

interface CustomCardProps {
  line1?: JSX.Element | string;
  line2?: JSX.Element | string;
  line3?: JSX.Element | string;
  line4?: JSX.Element | string;
  restLines?: (JSX.Element | string)[];
  title?: JSX.Element | string;
  deleteConfirmationHeader?: JSX.Element | string;
  deleteConfirmationContent?: JSX.Element | string;
  onClick?: () => void;
  onDelete?: () => void;
  isOpenButtonVisible?: boolean;
  isDeleteButtonVisible?: boolean;
  isCardClickDisabled?: boolean;
}

const useStyles = makeStyles({
  card: {
    minHeight: 160,
  },
  header: {
    paddingTop: 5,
    paddingBottom: 0,
    height: 0,
  },
  title: {
    fontSize: 14,
  },
  pos: {
    marginBottom: 12,
  },
  restLine: {
    marginTop: 12,
  },
});

const CustomCard: React.FunctionComponent<CustomCardProps> = ({
  deleteConfirmationHeader,
  deleteConfirmationContent,
  isDeleteButtonVisible,
  isOpenButtonVisible,
  ...card
}) => {
  const classes = useStyles();
  const [isDeleteDialogOpen, showDeleteDialog] = useState(false);
  const emptyLinePlaceHolder = "--";

  let headerClass = classes.header;
  if (card.title) {
    headerClass = "";
  }

  const cardHeader = (
    <CardHeader
      className={headerClass}
      title={card.title}
      action={
        <React.Fragment>
          {isOpenButtonVisible && (
            <IconButton onClick={card.onClick}>
              <Launch />
            </IconButton>
          )}
          {isDeleteButtonVisible && (
            <IconButton onClick={() => showDeleteDialog(true)}>
              <Close />
            </IconButton>
          )}
        </React.Fragment>
      }
    />
  );

  const cardContent = (
    <CardContent>
      <Typography className={classes.title} color="textSecondary" gutterBottom>
        {card.line1 || emptyLinePlaceHolder}
      </Typography>
      <Typography variant="h6">{card.line2 || emptyLinePlaceHolder}</Typography>
      <Typography className={classes.pos} color="textSecondary">
        {card.line3 || emptyLinePlaceHolder}
      </Typography>
      <Typography variant="body2" component="div">
        {card.line4 || emptyLinePlaceHolder}
      </Typography>
      {card.restLines?.map((line, index) => (
        <Typography variant="body2" component="div" key={index} className={classes.restLine}>
          {line || emptyLinePlaceHolder}
        </Typography>
      ))}
    </CardContent>
  );

  return (
    <React.Fragment>
      <Card>
        {(isOpenButtonVisible || isDeleteButtonVisible) && cardHeader}
        {card.isCardClickDisabled ? (
          <div className={classes.card}>{cardContent}</div>
        ) : (
          <CardActionArea className={classes.card} onClick={card.onClick} disabled={card.isCardClickDisabled}>
            {cardContent}
          </CardActionArea>
        )}
      </Card>

      {isDeleteButtonVisible && (
        <CustomDraggableDialog
          acceptText={<TranslatedMessage id="common.accept" />}
          cancelText={<TranslatedMessage id="common.cancel" />}
          content={deleteConfirmationContent || <TranslatedMessage id="common.confirmDeleteDescription" />}
          isOpen={isDeleteDialogOpen}
          onAccept={card.onDelete!}
          onCancel={() => showDeleteDialog(false)}
          title={deleteConfirmationHeader || <TranslatedMessage id="common.attention" />}
        />
      )}
    </React.Fragment>
  );
};

CustomCard.defaultProps = {
  onClick: () => {},
  onDelete: () => {},
} as Partial<CustomCardProps>;

export default CustomCard;
